import React, { useState, useEffect } from 'react';
import { COLORS } from '../utils/constants';
import Loading from './Loading';
import PinKeypad from './PinKeypad';
import { verifyPin, hasFaceConfigured } from '../services/securityService';
import { logAccess } from '../services/accessLogService';
import { useAuth } from '../context/AuthContext';

const FaceLogin = React.lazy(() => import('./FaceLogin'));

/**
 * Full-screen overlay displayed when the app is locked due to inactivity.
 *
 * Props:
 *   onUnlock - called when the user successfully authenticates
 */
const LockScreen = ({ onUnlock }) => {
  const { user } = useAuth();

  // Determine the default tab based on what's configured
  const [mode, setMode] = useState(null); // 'face' | 'pin' — set after checking config
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    const pickDefault = async () => {
      const face = await hasFaceConfigured();
      setMode(face ? 'face' : 'pin');
    };
    pickDefault();
  }, []);

  const handleFaceSuccess = async () => {
    await logAccess(user?.uid, 'facial', true);
    onUnlock();
  };

  const handlePinComplete = async (pin) => {
    const valid = await verifyPin(pin);
    if (valid) {
      await logAccess(user?.uid, 'pin', true);
      onUnlock();
    } else {
      await logAccess(user?.uid, 'pin', false, 'PIN incorreto');
      setPinError('PIN incorreto. Tente novamente.');
      setTimeout(() => setPinError(''), 2500);
    }
  };

  // Don't render until we know which tab to show
  if (mode === null) return null;

  return (
    <div style={s.backdrop}>
      <div style={s.card}>
        <div style={s.icon}>🔒</div>
        <h2 style={s.title}>App Bloqueado</h2>
        <p style={s.subtitle}>Autentique-se para continuar</p>

        {/* Tab switcher */}
        <div style={s.tabs}>
          <button
            style={mode === 'face' ? { ...s.tab, ...s.tabActive } : s.tab}
            onClick={() => setMode('face')}
          >
            👤 Facial
          </button>
          <button
            style={mode === 'pin' ? { ...s.tab, ...s.tabActive } : s.tab}
            onClick={() => setMode('pin')}
          >
            🔑 PIN
          </button>
        </div>

        {mode === 'face' ? (
          <React.Suspense fallback={<Loading />}>
            <FaceLogin
              mode="login"
              onSuccess={handleFaceSuccess}
              onFallback={() => setMode('pin')}
            />
          </React.Suspense>
        ) : (
          <>
            {pinError ? <p style={s.errorMsg}>{pinError}</p> : null}
            <PinKeypad
              title="Digite seu PIN de 6 dígitos"
              onComplete={handlePinComplete}
            />
          </>
        )}
      </div>
    </div>
  );
};

const s = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(10,14,26,0.98)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    padding: 16,
    overflowY: 'auto',
  },
  card: {
    backgroundColor: COLORS.backgroundCard,
    borderRadius: 16,
    padding: '24px 20px',
    width: '100%',
    maxWidth: 400,
    border: `1px solid ${COLORS.border}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
  },
  icon: {
    fontSize: 48,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: COLORS.text,
    margin: 0,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: COLORS.textSecondary,
    margin: 0,
    textAlign: 'center',
  },
  tabs: {
    display: 'flex',
    gap: 8,
    width: '100%',
    marginTop: 4,
  },
  tab: {
    flex: 1,
    padding: '10px 8px',
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    backgroundColor: 'transparent',
    color: COLORS.textSecondary,
    fontSize: 14,
    fontWeight: '500',
    cursor: 'pointer',
  },
  tabActive: {
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: `1px solid ${COLORS.primary}`,
  },
  errorMsg: {
    color: COLORS.danger,
    fontSize: 14,
    textAlign: 'center',
    margin: 0,
  },
};

export default LockScreen;
